import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Info as Info } from '@mui/icons-material';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { Tooltip } from '@mui/material';
import '../../../stylesheet.css';
import { useLocation } from 'react-router-dom';

function PurchaseOrderIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const history = useHistory();
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [isFocused, setIsFocused] = useState(false);
    const branchId = 0;
    const location = useLocation();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    };

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, token, logout]);

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';
        if (location.state?.from === '/PurchaseOrderCreate' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        } else {
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);

    const fetchtableData = async () => {
        setData([]);
        try {
            const response = await fetchData('PurchaseOrderApi', `/PurchaseOrderDetails/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Purchase Order Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const handleDelete = async () => {
        deleteData('PurchaseOrderApi', `${deleteItemId}`, token)
            .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch((error) => console.error('Error deleting record:', error));
    };

    const handleActivate = (itemId) => {
        activateData('PurchaseOrderApi', `${itemId}`, token)
            .then(() => fetchtableData())
            .catch((error) => console.error('Error activating record:', error));
    };

    const openConfirmDialog = (itemId) => { setDeleteItemId(itemId); setConfirmDialogOpen(true); };
    const closeConfirmDialog = () => { setDeleteItemId(null); setConfirmDialogOpen(false); };

    function formatDate(dateString) {
        return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }

    return (
        <div className="w-full mx-auto" style={{ width: "100%", marginTop: "-5px" }}>
            <div className="flex items-center font-serif gap-3 p-2 rounded-xl shadow-sm" style={{ background: "#f9fafb", marginBottom: "10px" }}>
                <h6 className="uppercase tracking-wide font-serif text-black mt-2 text-gray-700 flex-grow" style={{ fontSize: "14px", fontWeight: "500" }}>
                    Purchase Order
                </h6>

                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{ width: isFocused ? "240px" : "180px", height: "30px", border: "1px solid #ddd", borderRadius: "20px", padding: "0 14px", fontSize: "12px", transition: "all 0.3s ease", outline: "none" }}
                />

                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                    style={{ height: "30px", fontSize: "12px", border: "1px solid #ddd", borderRadius: "6px", padding: "0 6px", fontFamily: 'Arial, sans-serif' }} />

                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                    style={{ height: "30px", fontSize: "12px", border: "1px solid #ddd", borderRadius: "6px", padding: "0 6px", fontFamily: 'Arial, sans-serif' }} />

                <button onClick={fetchtableData}
                    style={{ background: "#1090a3", color: "white", fontSize: "12px", border: "none", borderRadius: "20px", padding: "4px 14px", cursor: "pointer" }}
                    onMouseOver={(e) => (e.currentTarget.style.background = "#0b6d7a")}
                    onMouseOut={(e) => (e.currentTarget.style.background = "#1090a3")}>
                    Refresh
                </button>

                {hasCreateMasterPermission && (
                    <Tooltip title="Create New Purchase Order" arrow>
                        <Button component={Link} to="/PurchaseOrderCreate"
                            style={{ background: "#3F4F44", color: "white", fontSize: "12px", border: "none", borderRadius: "20px", padding: "4px 14px", cursor: "pointer", fontFamily: 'inherit' }}>
                            Create
                        </Button>
                    </Tooltip>
                )}
            </div>

            <div className="rounded-lg overflow-hidden shadow-sm" style={{ maxHeight: "480px", overflowY: "auto", marginTop: '-10px' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {["Sl.No", "Bill Date", "Bill No", "Supplier", "Amount", "CGST", "SGST", "IGST", "Total", "Qty", ""].map((head) => (
                                    <TableCell key={head} style={{
                                        fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
                                        borderBottom: "2px solid #1e293b", color: "#f8fafc", padding: '2px',
                                        textAlign: ["Amount", "CGST", "SGST", "IGST", "Total", "Qty"].includes(head) ? "right" : "left",
                                        backgroundColor: "#0f172a", letterSpacing: "0.5px"
                                    }}>
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: '#F1EFEC' }}>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.Id} className={item.IsActive ? '' : 'inactiveRow'}
                                    sx={{ '&:hover': { backgroundColor: 'white' }, cursor: 'pointer' }}>
                                    <TableCell style={{ padding: '3px' }}>{index + 1}</TableCell>
                                    <TableCell style={{ padding: '3px' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell style={{ padding: '3px' }}>{item.BillNumber}</TableCell>
                                    <TableCell style={{ padding: '3px' }}>{item.SupplierName}</TableCell>
                                    <TableCell style={{ padding: '3px', textAlign: 'right' }}>{item.Amount}</TableCell>
                                    <TableCell style={{ padding: '3px', textAlign: 'right' }}>{item.CGSTAmount}</TableCell>
                                    <TableCell style={{ padding: '3px', textAlign: 'right' }}>{item.SGSTAmount}</TableCell>
                                    <TableCell style={{ padding: '3px', textAlign: 'right' }}>{item.IGSTAmount}</TableCell>
                                    <TableCell style={{ padding: '3px', textAlign: 'right' }}>{item.TotalAmount}</TableCell>
                                    <TableCell style={{ padding: '3px', textAlign: 'right' }}>{item.TotalQuantity}</TableCell>
                                    <TableCell style={{ padding: '3px', textAlign: 'right' }}>
                                        <Tooltip title="Order Details" arrow>
                                            <IconButton component={Link} to={`/PurchaseOrderDetails/${item.Id}`} color="warning">
                                                <Info style={{ fontSize: '19px' }} />
                                            </IconButton>
                                        </Tooltip>
                                        {hasEditMasterPermission && item.IsActive && (
                                            <Tooltip title="Edit Order" arrow>
                                                <IconButton component={Link} to={`/PurchaseOrderEdit/${item.Id}`} color="primary">
                                                    <EditIcon style={{ fontSize: '19px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {hasDeleteMasterPermission && item.IsActive && (
                                            <Tooltip title="Delete Order" arrow>
                                                <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                                                    <DeleteIcon style={{ fontSize: '19px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!item.IsActive && (
                                            <Tooltip title="Activate Order" arrow>
                                                <IconButton onClick={() => handleActivate(item.Id)} color="success">
                                                    <ActivateIcon style={{ fontSize: '19px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>{"Delete Record?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this record?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <button onClick={closeConfirmDialog} style={{ backgroundColor: '#dc3545', color: '#fff', border: '1px solid #dc3545', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', marginRight: '8px' }}>Cancel</button>
                    <button onClick={handleDelete} style={{ backgroundColor: '#28a745', color: '#fff', border: '1px solid #28a745', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>Confirm</button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default PurchaseOrderIndex;
